import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { useEffect } from 'react';
import {
  checkIfUserHasPremiumOnAnyAccount,
  openCheckoutPage,
  OpenCheckoutPageParams,
} from '../utilities/paywallHelper';
import { fetchOffering } from '../features/offering/offeringSlice';
import { checkForPaidCheckoutSession } from '../app/logPaidCheckoutSession';
import { useAppDispatch } from '../app/hooks';
import { NUMBER_OF_FREE_UNSUBSCRIBES, NUMBER_OF_FREE_DELETES } from '../featureFlags';
import { getCurrentMailbox, getCurrentMailboxQuery } from '../model/mailbox';
import {
  FORCE_PAYWALL,
  isPaywallDisabled,
  ALLOW_UNLIMITED_UNSUBSCRIBES,
  ALLOW_UNLIMITED_DELETES,
} from '../paywallFlags';
import { useLiveQuery } from 'dexie-react-hooks';

function usPremiumStatus(): PaywallContextValues {
  const [hasPremium, setHasPremium] = useState<boolean>(false);
  const [hasProvisionalPremium, setHasProvisionalPremium] = useState<boolean>(false);

  const [isLoadingPremiumStatus, setIsLoadingPremiumStatus] = useState<boolean>(true);

  const dispatch = useAppDispatch();

  const fetchPremiumStatus = useCallback(async () => {
    const [hasJustPaid, userHasPremium] = await Promise.all([
      checkForPaidCheckoutSession(),
      checkIfUserHasPremiumOnAnyAccount(),
    ]);

    if (hasJustPaid) {
      setHasProvisionalPremium(true);
      setHasPremium(true);
      setIsLoadingPremiumStatus(false);
    } else if (userHasPremium) {
      setHasPremium(true);
      setIsLoadingPremiumStatus(false);
    } else if (!hasProvisionalPremium) {
      setHasPremium(false);
      setIsLoadingPremiumStatus(false);
    }
  }, [hasProvisionalPremium]);

  const configureIAP = useCallback(async () => {
    dispatch(fetchOffering());
  }, []);

  const purchasePremium = useCallback(async (params: OpenCheckoutPageParams) => {
    openCheckoutPage(params);
  }, []);

  const mailbox = useLiveQuery(getCurrentMailboxQuery);

  useEffect(() => {
    configureIAP()
      .then(() => {
        fetchPremiumStatus();
      })
      .catch(console.log);
  }, [fetchPremiumStatus, mailbox?.credential]);

  // CAUTION: This function is called every single time the user performs an email action. Be careful about what you put in here.
  const shouldShowPaywallWhenUnsubscribing = useCallback(async () => {
    if (FORCE_PAYWALL) {
      return false;
    }

    if (isPaywallDisabled()) {
      return true;
    }

    if (hasPremium) {
      return false;
    }

    const mailbox = await getCurrentMailbox();
    if (!mailbox) {
      throw new Error('Mailbox not found!');
    }

    const user = await mailbox.getUser();
    if (!user) {
      throw new Error('User not found!');
    }

    if (
      !ALLOW_UNLIMITED_UNSUBSCRIBES &&
      (user?.unsubscribeCount || 0) >= NUMBER_OF_FREE_UNSUBSCRIBES
    ) {
      return true;
    }

    if (!ALLOW_UNLIMITED_DELETES && (user?.trashedThreadCount || 0) >= NUMBER_OF_FREE_DELETES) {
      return true;
    }

    return false;
  }, [hasPremium]);

  return {
    hasPremium,
    isLoadingPremiumStatus,
    purchasePremium,
    shouldShowPaywallWhenUnsubscribing,
  };
}

export type PaywallContextValues = {
  hasPremium: boolean;
  isLoadingPremiumStatus: boolean;
  purchasePremium: (checkoutParams: OpenCheckoutPageParams) => Promise<void>;
  shouldShowPaywallWhenUnsubscribing: () => Promise<boolean>;
};

const PaywallContext = createContext<PaywallContextValues>(null as any);

type PaywallProviderProps = {
  children: ReactNode;
};

export const PaywallProvider: React.FC<PaywallProviderProps> = ({ children }) => {
  const {
    hasPremium,
    isLoadingPremiumStatus,
    purchasePremium,
    shouldShowPaywallWhenUnsubscribing,
  } = usPremiumStatus();

  const value: PaywallContextValues = {
    hasPremium,
    isLoadingPremiumStatus,
    purchasePremium,
    shouldShowPaywallWhenUnsubscribing,
  };

  return <PaywallContext.Provider value={value}>{children}</PaywallContext.Provider>;
};

// Custom hook to use the Paywall context
export const usePaywall = () => {
  const context = useContext(PaywallContext);
  if (context === undefined) {
    throw new Error('usePaywall must be used within a PaywallProvider');
  }
  return context;
};
